import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { companyInfo } from '../../utils/dataInfo';

// Helper function to get room name
const getRoomName = (roomId, roomsData) => {
    if (!roomsData || !Array.isArray(roomsData)) return 'Unknown';
    const room = roomsData.find(r => parseInt(r.id) === parseInt(roomId));
    return room ? room.roomtype : 'Unknown';
};

// Helper function to get status name
const getStatusName = (statusId) => {
    switch (parseInt(statusId)) {
        case 1: return 'Active';
        case 2: return 'Checked In';
        case 3: return 'Checked Out';
        case 0: return 'Cancelled';
        default: return 'Unknown';
    }
};

// Helper function to get booking type name
const getBookingTypeName = (bookingType) => {
    switch (parseInt(bookingType)) {
        case 1: return 'Individual';
        case 2: return 'Organisation';
        default: return 'Unknown';
    }
};

// Helper function to format currency
const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '0';
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount) + " sh";
};

// Helper function to format date
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

// Helper function to split text into multiple lines
const splitTextIntoLines = (doc, text, maxWidth) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const width = doc.getTextWidth(currentLine + ' ' + word);
        if (width < maxWidth) {
            currentLine += ' ' + word;
        } else {
            lines.push(currentLine);
            currentLine = word;
        }
    }
    lines.push(currentLine);
    return lines;
};

// Manual table drawing function for PDF with selected columns
const drawBookingsTable = (doc, data, startY, pageWidth) => {
    const margin = 14;
    const rowHeight = 12;

    // Column widths: Booking #, Customer/Organisation, Type, Check In, Check Out, Room Type, Amount, Status
    const colWidths = [45, 45, 25, 25, 25, 30, 30, 25];

    // Check if we need a new page
    let currentY = startY;
    const neededHeight = (data.length + 1) * rowHeight;
    if (currentY + neededHeight > doc.internal.pageSize.height - 20) {
        doc.addPage('l');
        currentY = margin;
    }

    // Draw table header
    doc.setFillColor(255, 255, 255);
    doc.setTextColor(25, 118, 210);
    doc.setFont(undefined, 'bold');

    const headers = ['Booking #', 'Customer/Organisation', 'Type', 'Check In', 'Check Out', 'Room Type', 'Amount', 'Status'];
    let xPos = margin;

    // Draw header background
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

    // Draw header text
    headers.forEach((header, index) => {
        const cellX = xPos + 2;
        doc.text(header, cellX, currentY + 7);
        xPos += colWidths[index];
    });

    // Draw header underline
    doc.setDrawColor(25, 118, 210);
    doc.setLineWidth(0.5);
    doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);

    currentY += rowHeight;

    // Draw table rows
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);

    data.forEach((row, rowIndex) => {
        // Calculate needed height for this row
        let maxLines = 1;
        const cellLines = row.map((cell, cellIndex) => {
            const text = String(cell);
            const maxWidth = colWidths[cellIndex] - 4;
            const lines = splitTextIntoLines(doc, text, maxWidth);
            maxLines = Math.max(maxLines, lines.length);
            return lines;
        });

        const actualRowHeight = rowHeight * maxLines;

        // Check for page break
        if (currentY + actualRowHeight > doc.internal.pageSize.height - 20) {
            doc.addPage('l');
            currentY = margin;
            
            // Redraw header on new page
            doc.setFillColor(255, 255, 255);
            doc.setTextColor(25, 118, 210);
            doc.setFont(undefined, 'bold');
            
            doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
            
            xPos = margin;
            headers.forEach((header, index) => {
                doc.text(header, xPos + 2, currentY + 7);
                xPos += colWidths[index];
            });
            
            doc.setDrawColor(25, 118, 210);
            doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
            
            currentY += rowHeight;
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);
        }
        
        // Alternate row colors
        if (rowIndex % 2 === 0) {
            doc.setFillColor(248, 248, 248);
        } else {
            doc.setFillColor(255, 255, 255);
        }
        
        // Draw row background
        doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
        
        // Draw row text with multi-line support
        cellLines.forEach((lines, cellIndex) => {
            xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
            
            lines.forEach((line, lineIndex) => {
                const lineY = currentY + 7 + (lineIndex * rowHeight);
                doc.text(line, xPos + 2, lineY);
            });
        });
        
        currentY += actualRowHeight;
    });

    return currentY;
};

// Helper function to get customer/organisation display name
const getCustomerDisplayName = (booking) => {
    if (booking.booking_type === 2) {
        return booking.organisation_name || 'N/A';
    }
    return booking.customer_name || 'N/A';
};

// Helper function to format organisation members
const formatOrganisationMembers = (members) => {
    if (!members || members.length === 0) return 'N/A';
    return members.map(m => `${m.name} (${m.phone}) - Room: ${getRoomName(m.room_type)}`).join('; ');
};

// Excel export for Bookings with all attributes
export const exportToExcel = (bookings, roomsData = []) => {
    try {
        // Prepare data for Excel with all attributes
        const excelData = bookings.map((booking, index) => {
            // Base data
            const baseData = {
                'No.': index + 1,
                'Booking ID': booking.id,
                'Booking Number': booking.booking_no || `BK${booking.id.toString().padStart(5, '0')}`,
                'Booking Type': getBookingTypeName(booking.booking_type),
                'Check In': booking.check_in ? new Date(booking.check_in).toLocaleString() : 'N/A',
                'Check Out': booking.check_out ? new Date(booking.check_out).toLocaleString() : 'N/A',
                'Room Type': getRoomName(booking.room_type, roomsData),
                'Adults': booking.adults || 1,
                'Children': booking.children || 0,
                'Booking Charge': formatCurrency(booking.booking_charge),
                'Discount Amount': formatCurrency(booking.discount_amount),
                'Discount Reason': booking.discount_reason || 'N/A',
                'Commission Rate': `${booking.commission_rate || 0}%`,
                'Commission Amount': formatCurrency(booking.commission_amount),
                'Total Amount': formatCurrency(booking.total_amount),
                'Advance Amount': formatCurrency(booking.advance_amount),
                'Balance Due': formatCurrency((booking.total_amount || 0) - (booking.advance_amount || 0)),
                'Payment Mode': booking.payment_mode || 'N/A',
                'Bank Name': booking.bank_name || 'N/A',
                'Card/Account No': booking.card_no || 'N/A',
                'Mobile Money Phone': booking.mobile_money_phone || 'N/A',
                'Mobile Money Ref': booking.mobile_money_ref || 'N/A',
                'Arrival From': booking.arrival_from || 'N/A',
                'Purpose of Visit': booking.purpose_of_visit || 'N/A',
                'Remarks': booking.booking_remarks || 'N/A',
                'Advance Remarks': booking.advance_remarks || 'N/A',
                'Status': getStatusName(booking.status),
                'Created Date': new Date(booking.created_at).toLocaleDateString(),
                'Updated Date': new Date(booking.updated_at).toLocaleDateString()
            };

            // Add type-specific fields
            if (booking.booking_type === 1) { // Individual
                return {
                    ...baseData,
                    'Customer Name': booking.customer_name || 'N/A',
                    'Customer Mobile': booking.customer_mobile || 'N/A',
                    'Customer Email': booking.customer_email || 'N/A',
                    'Organisation Name': 'N/A',
                    'Organisation Members': 'N/A'
                };
            } else { // Organisation
                return {
                    ...baseData,
                    'Customer Name': 'N/A',
                    'Customer Mobile': 'N/A',
                    'Customer Email': 'N/A',
                    'Organisation Name': booking.organisation_name || 'N/A',
                    'Organisation Members': formatOrganisationMembers(booking.organisation_members)
                };
            }
        });

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Auto-size columns
        const maxWidth = excelData.reduce((widths, row) => {
            Object.keys(row).forEach((key, idx) => {
                const length = String(row[key]).length;
                if (!widths[idx] || length > widths[idx]) {
                    widths[idx] = length;
                }
            });
            return widths;
        }, []);
        
        worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Bookings');

        // Generate Excel file and download
        const fileName = `bookings_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Bookings Excel export failed:', error);
        throw new Error('Failed to export to Excel: ' + error.message);
    }
};

// Enhanced PDF export for Bookings with selected columns only (in landscape mode)
export const exportToPDFEnhanced = (bookings, roomsData = [], filters = {}) => {
    try {
        // Create PDF in landscape mode
        const doc = new jsPDF('l', 'mm', 'a4');
        
        // Set document properties
        doc.setProperties({
            title: 'Bookings Report',
            subject: 'Bookings Export',
            author: companyInfo.name,
            keywords: 'bookings, reservation, export, report',
            creator: companyInfo.name
        });
        
        // Get landscape page width and height
        const pageWidth = doc.internal.pageSize.width;
        
        // Title section
        doc.setFontSize(20);
        doc.setTextColor(25, 118, 210);
        doc.text('BOOKINGS REPORT', pageWidth / 2, 25, { align: 'center' });
        
        // Generation details
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        let currentY = 35;
        doc.text(`Report Generated: ${new Date().toLocaleString()}`, 14, currentY);
        currentY += 6;
        doc.text(`Total Bookings: ${bookings.length}`, 14, currentY);
        currentY += 6;
        
        // Filters applied
        if (filters.booking_no || filters.dateFrom || filters.dateTo || filters.room_type || filters.status || filters.customer_name) {
            doc.text('Applied Filters:', 14, currentY);
            currentY += 6;
            
            if (filters.booking_no) {
                doc.text(`• Booking Number: ${filters.booking_no}`, 20, currentY);
                currentY += 6;
            }
            if (filters.customer_name) {
                doc.text(`• Customer/Organisation: ${filters.customer_name}`, 20, currentY);
                currentY += 6;
            }
            if (filters.room_type) {
                const roomName = getRoomName(filters.room_type, roomsData);
                doc.text(`• Room Type: ${roomName}`, 20, currentY);
                currentY += 6;
            }
            if (filters.status) {
                doc.text(`• Status: ${getStatusName(filters.status)}`, 20, currentY);
                currentY += 6;
            }
            if (filters.dateFrom) {
                doc.text(`• From date: ${formatDate(filters.dateFrom)}`, 20, currentY);
                currentY += 6;
            }
            if (filters.dateTo) {
                doc.text(`• To date: ${formatDate(filters.dateTo)}`, 20, currentY);
                currentY += 6;
            }
            currentY += 6;
        }
        
        // Prepare table data with selected columns
        const tableData = bookings.map((booking) => [
            booking.booking_no || `BK${booking.id.toString().padStart(5, '0')}`,
            getCustomerDisplayName(booking),
            getBookingTypeName(booking.booking_type),
            formatDate(booking.check_in),
            formatDate(booking.check_out),
            getRoomName(booking.room_type, roomsData),
            formatCurrency(booking.total_amount || 0),
            getStatusName(booking.status)
        ]);

        // Draw the table
        drawBookingsTable(doc, tableData, currentY + 5, pageWidth);
        
        const fileName = `bookings_report_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Bookings PDF export failed:', error);
        throw new Error('Failed to export to PDF: ' + error.message);
    }
};

// CSV export for Bookings with all attributes
export const exportToCSV = (bookings, roomsData = []) => {
    try {
        // Prepare data for CSV
        const csvRows = [];
        
        // Headers
        const headers = [
            'Booking ID',
            'Booking Number',
            'Booking Type',
            'Customer Name',
            'Customer Mobile',
            'Customer Email',
            'Organisation Name',
            'Organisation Members',
            'Check In',
            'Check Out',
            'Room Type',
            'Adults',
            'Children',
            'Booking Charge',
            'Discount Amount',
            'Discount Reason',
            'Commission Rate',
            'Commission Amount',
            'Total Amount',
            'Advance Amount',
            'Balance Due',
            'Payment Mode',
            'Bank Name',
            'Card/Account No',
            'Mobile Money Phone',
            'Mobile Money Ref',
            'Arrival From',
            'Purpose of Visit',
            'Remarks',
            'Advance Remarks',
            'Status',
            'Created Date',
            'Updated Date'
        ];
        
        csvRows.push(headers.join(','));
        
        // Data rows
        bookings.forEach(booking => {
            const row = [
                booking.id,
                `"${(booking.booking_no || `BK${booking.id.toString().padStart(5, '0')}`).replace(/"/g, '""')}"`,
                `"${getBookingTypeName(booking.booking_type).replace(/"/g, '""')}"`,
                `"${(booking.customer_name || 'N/A').replace(/"/g, '""')}"`,
                `"${(booking.customer_mobile || 'N/A').replace(/"/g, '""')}"`,
                `"${(booking.customer_email || 'N/A').replace(/"/g, '""')}"`,
                `"${(booking.organisation_name || 'N/A').replace(/"/g, '""')}"`,
                `"${formatOrganisationMembers(booking.organisation_members).replace(/"/g, '""')}"`,
                `"${(booking.check_in ? new Date(booking.check_in).toLocaleString() : 'N/A').replace(/"/g, '""')}"`,
                `"${(booking.check_out ? new Date(booking.check_out).toLocaleString() : 'N/A').replace(/"/g, '""')}"`,
                `"${getRoomName(booking.room_type, roomsData).replace(/"/g, '""')}"`,
                booking.adults || 1,
                booking.children || 0,
                formatCurrency(booking.booking_charge || 0),
                formatCurrency(booking.discount_amount || 0),
                `"${(booking.discount_reason || 'N/A').replace(/"/g, '""')}"`,
                `"${booking.commission_rate || 0}%"`,
                formatCurrency(booking.commission_amount || 0),
                formatCurrency(booking.total_amount || 0),
                formatCurrency(booking.advance_amount || 0),
                formatCurrency((booking.total_amount || 0) - (booking.advance_amount || 0)),
                `"${(booking.payment_mode || 'N/A').replace(/"/g, '""')}"`,
                `"${(booking.bank_name || 'N/A').replace(/"/g, '""')}"`,
                `"${(booking.card_no || 'N/A').replace(/"/g, '""')}"`,
                `"${(booking.mobile_money_phone || 'N/A').replace(/"/g, '""')}"`,
                `"${(booking.mobile_money_ref || 'N/A').replace(/"/g, '""')}"`,
                `"${(booking.arrival_from || 'N/A').replace(/"/g, '""')}"`,
                `"${(booking.purpose_of_visit || 'N/A').replace(/"/g, '""')}"`,
                `"${(booking.booking_remarks || 'N/A').replace(/"/g, '""')}"`,
                `"${(booking.advance_remarks || 'N/A').replace(/"/g, '""')}"`,
                `"${getStatusName(booking.status).replace(/"/g, '""')}"`,
                `"${new Date(booking.created_at).toLocaleDateString()}"`,
                `"${new Date(booking.updated_at).toLocaleDateString()}"`
            ];
            csvRows.push(row.join(','));
        });

        // Create CSV content
        const csvContent = csvRows.join('\n');

        // Create and download the file
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `bookings_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        return { success: true };
    } catch (error) {
        console.error('Bookings CSV export failed:', error);
        throw new Error('Failed to export to CSV: ' + error.message);
    }
};